import _ from "underscore";
import get from "lodash.get";
import { uncapitalizeExceptAllCaps } from "./utils";
import { calculateRiskDifference } from "./statisticalUtils";

export const calcAbsEffect = (a, b) => a - b;
export const toFixedNumber = (value, scale = 2) => {
  return (value = _.isNumber(value) ? parseFloat(value).toFixed(scale) : value);
};

export const formatNulls = value => {
  if (!_.isNumber(value)) {
    return "--";
  } else {
    return value;
  }
};

export const percentValue = value => {
  value = toFixedNumber(value, 1);
  return `${value}%`;
};

export const orderCIFromCITo = (ciFrom, ciTo) => ciFrom - ciTo;

export const formatAbsoluteDenominator = value => {
  if (_.isNumber(value)) {
    return Number(value).toLocaleString();
  } else {
    return "--";
  }
};

export const formatFewerMore = (value, percent = false, scale = 0) => {
  if (_.isNumber(value)) {
    var fewerMore = value < 0 ? "more" : "fewer";
    value = toFixedNumber(Math.abs(value), scale);
    value = percent ? percentValue(value) : Number(value).toLocaleString();
    return `${value} ${fewerMore}`;
  } else {
    return formatNulls(value);
  }
};

export const formatCerIfNaN = cer => (isNaN(cer) ? 0 : cer);

export const multiplyByDenominator = (cr, absDenominator) => {
  if (_.isNumber(absDenominator)) {
    return parseInt((cr * absDenominator).toFixed(0));
  } else {
    return "-";
  }
};

export const dichRiskDifference = (
  cer,
  effectSize,
  effectMeasure,
  confidenceIntervalFrom,
  confidenceIntervalTo,
  absDenominator,
  eventType
) => {
  var rd, rdFromVal, rdToVal;

  rd = "<span class='value-container'>";
  rd += "<span class='value'>";
  rd += Math.abs(
    multiplyByDenominator(cer, absDenominator) -
      multiplyByDenominator(
        calculateRiskDifference(cer, effectSize, effectMeasure, eventType),
        absDenominator
      )
  );
  rd += "</span>";
  rd += `<span class="per-denominator">per`;
  rd += ` ${formatAbsoluteDenominator(absDenominator)}`;
  rd += "</span>";
  rd += "</span>";
  rd += "<br>";

  rd += "<span class='difference-value'>";
  rd += formatFewerMore(
    multiplyByDenominator(
      calculateRiskDifference(cer, effectSize, effectMeasure, eventType),
      absDenominator
    )
  );
  rd += "</span>";
  rd += "(";
  rdFromVal = calculateRiskDifference(
    cer,
    confidenceIntervalFrom,
    effectMeasure,
    eventType
  );
  rdToVal = calculateRiskDifference(
    cer,
    confidenceIntervalTo,
    effectMeasure,
    eventType
  );
  [rdFromVal, rdToVal].sort(orderCIFromCITo);
  rd += formatFewerMore(multiplyByDenominator(rdFromVal, absDenominator));
  rd += ` to `;
  rd += formatFewerMore(multiplyByDenominator(rdToVal, absDenominator));
  rd += ")";
  return `<span class='cell-value'>${rd}</span>`;
};

export const interventionAbsCount = (attributes, baselineRisk) => {
  var a, cer;

  if (baselineRisk === "calculated")
    cer = formatCerIfNaN(attributes.controlCount / attributes.controlTotal);
  else cer = attributes[`${baselineRisk}ControlRisk`] / 100;

  a = attributes;
  return Math.abs(
    multiplyByDenominator(cer, a.absDenominator) -
      multiplyByDenominator(
        calculateRiskDifference(
          cer,
          a.effectSize,
          a.effectMeasure,
          a.eventType
        ),
        a.absDenominator
      )
  );
};

export const riskDifference = (attributes, baselineRisk) => {
  var a, cer;
  a = attributes;
  if (baselineRisk === "calculated")
    cer = formatCerIfNaN(attributes.controlCount / attributes.controlTotal);
  else cer = attributes[`${baselineRisk}ControlRisk`] / 100;

  return dichRiskDifference(
    cer,
    a.effectSize,
    a.effectMeasure,
    a.confidenceIntervalFrom,
    a.confidenceIntervalTo,
    a.absDenominator,
    a.eventType
  );
};

export const formatDichSofControl = (
  risk,
  absDenominator,
  outputDenominator = true
) => {
  var value;
  if (isNaN(risk)) {
    risk = 0;
  }
  value = _.isNumber(absDenominator)
    ? Number((risk * absDenominator).toFixed(0)).toLocaleString()
    : formatAbsoluteDenominator(absDenominator);
  if (outputDenominator) {
    return `<span class='value-container'>
      <span class="value">${value}</span>
      <span class="per-denominator">per ${formatAbsoluteDenominator(
        absDenominator
      )}</span>
    </span>`;
  }
};

export const comparisonAbsCount = (outcome, baselineRisk) => {
  var risk;
  if (baselineRisk === "calculated")
    risk = outcome.controlCount / outcome.controlTotal;
  else risk = outcome[`${baselineRisk}ControlRisk`] / 100;
  return _.isNaN(risk) ? 0 : Number((risk * outcome.absDenominator).toFixed(0));
};

export const dichSofControl = (
  attributes,
  baselineRisk,
  outputDenominator = true
) => {
  var risk;
  if (baselineRisk === "calculated")
    risk = attributes.controlCount / attributes.controlTotal;
  else risk = attributes[`${baselineRisk}ControlRisk`] / 100;
  return formatDichSofControl(
    risk,
    attributes.absDenominator,
    outputDenominator
  );
};

export const attrWithOtherValue = (outcome, attrName, otherAttrName) => {
  var ref;
  if ((ref = outcome[attrName]) === "other" || ref === "others") {
    return outcome[otherAttrName];
  } else {
    return outcome[attrName];
  }
};

export const translateCountableUnitsType = (value, countable) => {
  var translation;
  if (!value) {
    return "";
  }
  translation = countable
    ? value >= 0
      ? "higher"
      : "lower"
    : value >= 0
    ? "more"
    : "fewer";
  return translation;
};

export const contAbsoluteEffect = outcome => {
  var cif, cit, countable, effectSize, unitsType, value;
  effectSize = outcome.effectSize;
  cif = outcome.confidenceIntervalFrom;
  cit = outcome.confidenceIntervalTo;
  unitsType = outcome.unitsType;
  countable = unitsType === "countable" || _.isEmpty(unitsType);
  value = "<span class='value-container'>";
  value += `${attrWithOtherValue(outcome, "effectMeasure", "otherMeasure")} `;
  value += `<b>${Math.abs(effectSize)} `;
  if (outcome.units && outcome.units !== "") {
    value += `${outcome.units} `;
  }
  value += translateCountableUnitsType(outcome.effectSize, countable);
  value += `</b><br>(${Math.abs(cif)} `;
  value += translateCountableUnitsType(cif, countable);
  value += " ";
  value += "to";
  value += ` ${Math.abs(cit)} `;
  value += translateCountableUnitsType(cit, countable);
  value += ")";
  value += "</span>";
  return value;
};

export const contSofControl = (outcome, intervention) => {
  if (outcome.effectMeasure === "SMD") return "-";
  var contSofControl;
  contSofControl = `mean ${uncapitalizeExceptAllCaps(outcome.name)} `;

  contSofControl += "without";
  contSofControl += ` ${uncapitalizeExceptAllCaps(intervention)} `;
  if (outcome.changeScoreInControls === "range") {
    contSofControl += `ranged from `;
  } else {
    contSofControl += `was `;
  }
  contSofControl += "<strong>";
  if (outcome.changeScoreInControlsValue) {
    contSofControl += `${outcome.changeScoreInControlsValue}`;
  } else {
    contSofControl += 0;
  }
  contSofControl += "</strong> ";
  if (outcome.units) {
    contSofControl += ` ${outcome.units}`;
  }
  return contSofControl;
};

export const followUpOrTimingWithLabel = attributes => {
  if (!attributes.followUpLength || attributes.followUpLength === "0") {
    return null;
  }
  let followUp = "";
  if (get(attributes, ["designStudies", "value"]) === "case_control_studies") {
    followUp = "Timing of exposure".toLowerCase() + ": ";
  } else {
    followUp = "Follow up".toLowerCase() + ": ";
  }
  return (followUp += followUpOrTiming(attributes));
};

const followUpTimeUnits = {
  days: "days",
  weeks: "weeks",
  months: "months",
  years: "years",
  patientYears: "patient years",
  others: ""
};

export const followUpOrTiming = attributes => {
  var followUp;
  if (!attributes.followUpLength) {
    return;
  }
  followUp = "";
  if (attributes.followUpType) {
    followUp += attributes.followUpType;
  }
  followUp += ` ${attributes.followUpLength}`;
  if (attributes.followUpTimeUnit === "others") {
    if (attributes.othersFollowUpTimeUnit) {
      followUp += ` ${attributes.othersFollowUpTimeUnit}`;
    }
  } else if (attributes.followUpTimeUnit) {
    followUp += ` ${followUpTimeUnits[attributes.followUpTimeUnit]}`;
  }
  if (attributes.followUpType !== "range") {
    return followUp;
  }
  followUp += ` to`;
  if (attributes.followUpLengthTo) {
    followUp += ` ${attributes.followUpLengthTo}`;
  }
  if (attributes.followUpToTimeUnit === "others") {
    if (attributes.othersFollowUpToTimeUnit) {
      followUp += ` ${attributes.othersFollowUpToTimeUnit}`;
    }
  } else if (attributes.followUpToTimeUnit) {
    followUp += ` ${followUpTimeUnits[attributes.followUpToTimeUnit]}`;
  }
  return followUp;
};
